import { useEffect, useMemo, useState } from "react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowRight, CheckCircle, Info } from "@phosphor-icons/react";

/**
 * Diálogo para imputar cobros/pagos con saldo pendiente contra facturas.
 * - Muestra la lista de cobros/pagos con `monto_no_imputado > 0` de la
 *   contraparte seleccionada.
 * - Muestra la lista de facturas con saldo > 0 de la misma contraparte.
 * - El usuario tildá un cobro y las facturas destino; presiona "Imputar" y
 *   el backend aplica FIFO en el orden en que se tildan.
 */
export default function ImputarSaldosDialog({ open, onClose, contraparte, tipo, onDone }) {
  const isClientes = tipo === "cliente";
  const cpTipo = isClientes ? "cobro" : "pago";
  const [noImputados, setNoImputados] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selCobroId, setSelCobroId] = useState(null);
  const [selFacIds, setSelFacIds] = useState([]);

  const load = async () => {
    if (!contraparte) return;
    setLoading(true);
    try {
      const [ni, movs] = await Promise.all([
        api.get(`/cobros-pagos/no-imputados`, { params: { tipo: cpTipo, contraparte_id: contraparte.id } }),
        api.get(`${isClientes ? "/clientes" : "/proveedores"}/${contraparte.id}/movimientos`),
      ]);
      setNoImputados(ni.data || []);
      setFacturas(
        ((movs.data.facturas || []))
          .filter((f) => (f.saldo || 0) > 0.01)
          .sort((a, b) => {
            const va = a.fecha_vencimiento || a.fecha || "9999-12-31";
            const vb = b.fecha_vencimiento || b.fecha || "9999-12-31";
            return va.localeCompare(vb);
          })
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los saldos");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setSelCobroId(null);
      setSelFacIds([]);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contraparte?.id]);

  const selCobro = useMemo(
    () => noImputados.find((x) => x.id === selCobroId) || null,
    [noImputados, selCobroId]
  );

  const totalSelFacturas = useMemo(() => {
    return facturas
      .filter((f) => selFacIds.includes(f.id))
      .reduce((s, f) => s + (f.saldo || 0), 0);
  }, [facturas, selFacIds]);

  const toggleFactura = (fid) => {
    setSelFacIds((prev) =>
      prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]
    );
  };

  const submit = async () => {
    if (!selCobroId) return toast.error(`Elegí un ${isClientes ? "cobro" : "pago"} con saldo`);
    if (!selFacIds.length) return toast.error("Elegí al menos una factura destino");
    setSaving(true);
    try {
      const { data } = await api.post(`/cobros-pagos/${selCobroId}/imputar`, { facturas_ids: selFacIds });
      const nFac = (data.facturas || []).length;
      toast.success(
        `Imputado ${fmtMoney(data.aplicado_total)} a ${nFac} factura(s)` +
        (data.monto_no_imputado_restante > 0 ? ` · queda ${fmtMoney(data.monto_no_imputado_restante)} sin imputar` : "")
      );
      onDone?.();
      // Refrescar en caso de que quede saldo
      await load();
      setSelFacIds([]);
      // Si el cobro quedó totalmente imputado, deseleccionarlo
      if (data.monto_no_imputado_restante <= 0.01) setSelCobroId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo imputar el saldo");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="imputar-saldos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight size={22} weight="duotone" className="text-emerald-700" />
            Imputar saldos a favor · {contraparte?.nombre}
          </DialogTitle>
          <DialogDescription>
            Aplicá {isClientes ? "cobros" : "pagos"} recibidos sin factura (anticipos) contra facturas pendientes.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-neutral-500 py-8 text-center">Cargando…</div>
        ) : noImputados.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-neutral-50 rounded p-6 text-center text-sm text-neutral-600">
            <Info size={20} className="mx-auto mb-2 text-neutral-400" />
            No hay {isClientes ? "cobros" : "pagos"} con saldo pendiente de imputar para este {isClientes ? "cliente" : "proveedor"}.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase font-mono text-neutral-500 mb-2">
                {isClientes ? "Cobros" : "Pagos"} con saldo · elegí uno
              </div>
              <div className="border border-neutral-200 rounded max-h-80 overflow-y-auto">
                {noImputados.map((cp) => (
                  <label
                    key={cp.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${selCobroId === cp.id ? "bg-emerald-50" : ""}`}
                    data-testid={`imputar-cobro-${cp.id}`}
                  >
                    <input
                      type="radio"
                      name="cobro"
                      checked={selCobroId === cp.id}
                      onChange={() => { setSelCobroId(cp.id); setSelFacIds([]); }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {fmtDate(cp.fecha)} · <span className="text-neutral-500">{cp.caja_nombre}</span>
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {cp.concepto || (isClientes ? "Cobro" : "Pago")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-mono font-bold text-emerald-700">
                        {fmtMoney(cp.monto_no_imputado)}
                      </div>
                      <div className="text-[10px] font-mono text-neutral-500">
                        de {fmtMoney(cp.monto)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase font-mono text-neutral-500 mb-2">
                Facturas pendientes · elegí destino (FIFO por vto.)
              </div>
              <div className="border border-neutral-200 rounded max-h-80 overflow-y-auto">
                {facturas.length === 0 ? (
                  <div className="p-4 text-xs text-neutral-500 text-center">
                    No hay facturas pendientes para imputar.
                  </div>
                ) : facturas.map((f) => {
                  const disabled = !selCobroId;
                  return (
                    <label
                      key={f.id}
                      className={`flex items-center gap-3 px-3 py-2 border-b border-neutral-100 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-neutral-50"} ${selFacIds.includes(f.id) ? "bg-blue-50" : ""}`}
                      data-testid={`imputar-factura-${f.id}`}
                    >
                      <Checkbox
                        checked={selFacIds.includes(f.id)}
                        disabled={disabled}
                        onCheckedChange={() => !disabled && toggleFactura(f.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {f.tipo_comprobante || "Factura"} #{f.numero}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {fmtDate(f.fecha)}{f.fecha_vencimiento ? ` · vto ${fmtDate(f.fecha_vencimiento)}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono font-bold">{fmtMoney(f.saldo)}</div>
                        <div className="text-[10px] font-mono text-neutral-500">
                          de {fmtMoney(f.total)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              {selCobro && (
                <div className="mt-2 p-2 border border-emerald-200 bg-emerald-50 rounded text-xs font-mono text-emerald-900">
                  Disponible: <strong>{fmtMoney(selCobro.monto_no_imputado)}</strong>
                  {" · "}Seleccionado: <strong>{fmtMoney(Math.min(totalSelFacturas, selCobro.monto_no_imputado))}</strong>
                  {totalSelFacturas > selCobro.monto_no_imputado && (
                    <span className="ml-1 text-amber-800">(el resto queda pendiente)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="imputar-cancelar">
            Cerrar
          </Button>
          {noImputados.length > 0 && (
            <Button
              onClick={submit}
              disabled={saving || !selCobroId || selFacIds.length === 0}
              className="gap-1 bg-[#6B1F24] hover:bg-[#571519] text-white"
              data-testid="imputar-aplicar"
            >
              <CheckCircle size={14} weight="bold" /> {saving ? "Imputando…" : "Imputar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
